import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';
import { FileService } from '../../../core/services/FileService';
import { BasicButton } from './BasicButton';

// ==== Blot de imagen con clase img-fluid ====
const BaseImage = Quill.import('formats/image');
class ImageWithClass extends (BaseImage as any) {
  static create(value: any) {
    const node = super.create(value) as HTMLImageElement;
    node.classList.add('img-fluid');
    node.setAttribute('loading', 'lazy');
    return node;
  }
}
if (!(window as any).__quillImageWithClassRegistered) {
  Quill.register(ImageWithClass, true);
  (window as any).__quillImageWithClassRegistered = true;
}

interface Props {
  val: any;
  onChange: (html: string) => void;
  allowImage?: boolean;
}

export const MyEditor = ({ val, onChange, allowImage = false }: Props) => {
  // const [value, setValue] = useState<string>(val ?? '');
  const value = useRef<string>(val);
  const quillRef = useRef<ReactQuill | null>(null);
  const fileService = new FileService();

  // Timer para el auto-guardado
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);

  // Estabilizar onChange
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // ⚠️ Solo depende de "value" para evitar el bucle
  useEffect(() => {
    // onChange(value.current); // Comentado para evitar guardado inicial innecesario si no se desea
  }, [value]);

  // Limpieza del timer al desmontar
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  // const handleEditorChange = useCallback((html: string) => {
  //   onChange(value);

  // }, []);

  // Si el padre cambia "val" programáticamente, sincroniza sin re-render inútil
  useEffect(() => {
    // if (typeof val === 'string') 
    value.current = val;
  }, [val]); // intencionalmente sin "value" para no rebotar

  const ensureImagesFluid = useCallback((editor: any) => {
    if (!editor) return;
    const imgs: NodeListOf<HTMLImageElement> = editor.root.querySelectorAll('img');
    imgs.forEach(img => {
      if (!img.classList.contains('img-fluid')) img.classList.add('img-fluid');
      if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
    });
  }, []);

  const handleImageUploadManual = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      fileService.postFile(file, 'wysywy').subscribe(
        (resp: any) => {
          const uploadedFile = resp?.DataBeanProperties?.ObjectValue?.DataBeanProperties;
          const url = uploadedFile?.URL;
          if (!url) return;

          const editor = quillRef.current?.getEditor();
          const range = editor?.getSelection(true);
          if (editor && range) {
            editor.insertEmbed(range.index, 'image', url, 'user');
            editor.setSelection(range.index + 1, 0, 'user');
            setTimeout(() => ensureImagesFluid(editor), 0);
          }
        },
        (error: any) => console.error('Error al subir el archivo:', error)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureImagesFluid]);

  // Normaliza el html (idempotente) y dispara el cambio hacia arriba
  const handleChange = useCallback((html: string) => {
    const normalized = html
      // si no hay class, la agrega
      .replace(/<img\b((?:(?!\bclass=)[^>])*)>/gi, (_m, attrs) => `<img${attrs} class="img-fluid">`)
      // si ya hay class, añade img-fluid si falta
      .replace(/<img\b([^>]*\bclass=["'])([^"']*)(["'][^>]*>)?/gi, (_m, pre, classes, post = '>') =>
        classes.split(/\s+/).includes('img-fluid')
          ? `<img ${pre}${classes}${post}`
          : `<img ${pre}${classes} img-fluid${post}`
      );
    if (value.current != normalized) {
      value.current = normalized;

      // Auto-save logic (Debounce)
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      autoSaveTimer.current = setTimeout(() => {
        onChangeRef.current(normalized);
      }, 1000); // 1 segundo de espera
    }
  }, []);

  const handleBlur = useCallback(() => {
    // Al salir del foco, guardamos inmediatamente y cancelamos cualquier timer pendiente
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    onChangeRef.current(value.current);
  }, []);

  // Memoriza toolbar/modules/formats para estabilidad
  const toolbarOptions = useMemo(
    () => [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: [1, 2, 3, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ['link'],
      ['clean'],
    ],
    []
  );

  const modules = useMemo(
    () => ({
      toolbar: { container: toolbarOptions },
    }),
    [toolbarOptions]
  );

  const formats = useMemo(
    () => [
      'bold', 'italic', 'underline', 'strike',
      'header', 'list', 'bullet',
      'indent', 'align', 'color', 'background',
      'link',
      ...(allowImage ? ['image'] : []),
      'width', 'height' //Added width and height if needed for images
    ],
    [allowImage]
  );

  // “Policía” de imágenes
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    ensureImagesFluid(editor);

    const onTextChange = () => ensureImagesFluid(editor);
    editor.on('text-change', onTextChange);

    const clipboard: any = editor.getModule('clipboard');
    if (clipboard?.addMatcher) {
      clipboard.addMatcher('IMG', (_node: Node, delta: any) => {
        setTimeout(() => ensureImagesFluid(editor), 0);
        return delta;
      });
    }
    return () => {
      editor.off('text-change', onTextChange);
    };
  }, [ensureImagesFluid]);

  // Memoizing the ReactQuill component itself might be overkill if props are stable, 
  // but let's keep it simple first by just passing stable props.
  return (
    <div style={{ minHeight: 200 }}>
      <div className="row">
        <div className="col-md-12 text-end">
          {allowImage && (
            <button
              className="btn btn-icon waves-effect waves-light shadow-none cursor"
              title="Subir Imagen"
              type="button"
              onClick={handleImageUploadManual}
              style={{ marginBottom: -60, marginRight: 5, zIndex: 3, fontSize: '1.2rem' }}
            >
              <i className="ri-image-2-line"></i>
            </button>
          )}
        </div>

        <div className="col-md-12">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            defaultValue={val} // Use defaultValue for uncontrolled component initialized with val
            onChange={handleChange}
            onBlur={handleBlur}
            modules={modules}
            formats={formats}
            style={{ minHeight: 200 }}
          />
          {/* <BasicButton icon='' eventClick={() => { onChange(value.current) }}>Guardar Cambios</BasicButton> */}
        </div>
      </div>
    </div>
  );
};
